import { DashboardSettings } from './types';
import {
  decimalToHHMM as baseDecimalToHHMM,
  hhmmToDecimal as baseHhmmToDecimal,
  type TimeFormatOptions,
} from '@/utils/time-format';
import {
  calculateTargetProgress as baseCalculateTargetProgress,
} from '@/utils/date';

/**
 * Dashboard-specific time conversion that respects DashboardSettings.
 * Delegates to the shared utility in @/utils/time-format.
 */
export const decimalToHHMM = (hours: number | string, settings?: DashboardSettings): string => {
  const opts: TimeFormatOptions = {
    format: settings?.timeFormat === 'decimal' ? 'decimal' : 'hhmm',
    decimalPlaces: settings?.hoursRounding ?? 2,
    showSeconds: settings?.showSeconds ?? false,
    roundingPlaces: settings?.hoursRounding,
  };
  return baseDecimalToHHMM(hours, opts);
};

/** Re-export the shared hhmmToDecimal (identical behaviour). */
export const hhmmToDecimal = baseHhmmToDecimal;

export const formatTimeForDisplay = (
  hours: number | string, 
  settings: DashboardSettings
): string => {
  const numHours = typeof hours === 'string' ? parseFloat(hours) : hours;
  
  if (settings.timeFormat === 'decimal') {
    return numHours.toFixed(settings.hoursRounding);
  }
  
  return decimalToHHMM(numHours, settings);
};

export const parseTimeInput = (
  input: string, 
  settings: DashboardSettings
): number => {
  if (!input.trim()) return 0;
  
  // If it contains : it's in HH:MM format
  if (input.includes(':')) {
    return hhmmToDecimal(input);
  }
  
  // Otherwise treat as decimal
  // Future enhancement: could use settings.locale for number parsing
  const parsed = parseFloat(input);
  return isNaN(parsed) ? 0 : Math.min(parsed, settings.maxHoursEntry);
};

export const validateHoursInput = (
  hours: number,
  settings: DashboardSettings
): { isValid: boolean; message?: string } => {
  if (hours < settings.minHoursEntry) {
    return {
      isValid: false,
      message: `Minimum hours entry is ${settings.minHoursEntry}`
    };
  }
  
  if (hours > settings.maxHoursEntry) {
    return {
      isValid: false,
      message: `Maximum hours entry is ${settings.maxHoursEntry}`
    };
  }
  
  return { isValid: true };
};

export const getProgressColor = (
  percentage: number,
  settings: DashboardSettings
): { color: string; extraStyles?: string } => {
  const { progressColors } = settings;
  
  if (percentage >= 125) {
    return {
      color: progressColors.outstanding,
      extraStyles: "animate-pulse bg-gradient-to-r shadow-xl border border-white border-opacity-50"
    };
  } else if (percentage >= 120) {
    return {
      color: progressColors.outstanding,
      extraStyles: "bg-gradient-to-r shadow-lg animate-pulse border-t"
    };
  } else if (percentage >= 115) {
    return {
      color: progressColors.excellent,
      extraStyles: "bg-gradient-to-r shadow-md animate-pulse"
    };
  } else if (percentage >= 105) {
    return {
      color: progressColors.excellent,
      extraStyles: "bg-gradient-to-r shadow-sm animate-[pulse_2s_ease-in-out_infinite]"
    };
  } else if (percentage >= 100) {
    return {
      color: progressColors.excellent
    };
  } else if (percentage >= 80) {
    return {
      color: progressColors.good
    };
  } else if (percentage >= 60) {
    return {
      color: progressColors.good
    };
  } else if (percentage >= 40) {
    return {
      color: progressColors.fair
    };
  } else if (percentage >= 20) {
    return {
      color: progressColors.fair
    };
  }
  
  return {
    color: progressColors.poor
  };
};

/** Re-export from shared date utilities. */
export const calculateTargetProgress = baseCalculateTargetProgress;

export const formatCurrency = (
  amount: number,
  settings: DashboardSettings
): string => {
  return new Intl.NumberFormat(settings.locale, {
    style: 'currency',
    currency: settings.currency
  }).format(amount);
};

export const formatDate = (
  date: Date,
  settings: DashboardSettings
): string => {
  return new Intl.DateTimeFormat(settings.locale, {
    dateStyle: 'full'
  }).format(date);
};
